import React from 'react';

import { useSelector } from 'react-redux';
import { getStudentLoanData } from 'selectors/getStudentLoanData';
import FormNavigation from 'components/FormNavigation';
import { FlowComponentType } from 'routes/types';
import FormContainer from 'components/LoanForm/FormContainer';
import Button from 'components/Button';
import { StudentLoanApplyOnYourOwnResult } from 'enums/StudentLoanForgivenessFlowResults';
import CardSequence from 'components/Common/CardSequence';
import { ReactComponent as DebtIcon } from 'images/illustrative-icon-debt.svg';
import { ReactComponent as PaycheckIcon } from 'images/illustrative-icon-paycheck.svg';
import { ReactComponent as GiftIcon } from 'images/illustrative-icon-gift.svg';
import { Step } from 'components/Common/CardSequence/CardSequence';
import { addMonths } from 'utils/dateUtils';
import { OutcomeConfidence, PlanName, PlanShortName, PSLF } from 'api/StudentLoanApi';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';
import StudentLoanLoaderWrapper from 'components/StudentLoanForgiveness/LoaderWrapper';

import styles from './ApplyOnYourOwn.module.scss';

const ApplyOnYourOwn = ({ navigationInfo, handleNext }: FlowComponentType) => {
  const {
    recommendedPath,
    currentPath,
    statePrograms,
    confidence,
    missingInputReasons,
    usedSalaryAsAgiProxy,
  } = useSelector(getStudentLoanData);

  const currentPlans = parsePlans(currentPath?.plans ?? []);
  const recommendedPlans = parsePlans(withoutSave(recommendedPath?.plans ?? []));

  const steps: Step[] = [];

  const requiresVerification =
    confidence === OutcomeConfidence.RequiresVerification ||
    usedSalaryAsAgiProxy ||
    (missingInputReasons?.length ?? 0) > 0;

  if (requiresVerification) {
    steps.push({
      title: 'Confirm details that need verification',
      body: (
        <div className={styles.stepBody}>
          <p>
            Some inputs still need verification (for example employer eligibility for PSLF, AGI, or StudentAid loan
            details). Treat savings estimates as provisional until those are confirmed on StudentAid.gov.
          </p>
          {missingInputReasons && missingInputReasons.length > 0 && (
            <ul className={styles.bulletList}>
              {missingInputReasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          )}
          {usedSalaryAsAgiProxy && (
            <p>
              <i>Income used for this estimate may be salary rather than AGI — verify with your tax return.</i>
            </p>
          )}
        </div>
      ),
      icon: <PaycheckIcon />,
    });
  }

  const shouldChangeIdrEnrollment =
    recommendedPlans.isIdr && arePaymentPlansDifferent(currentPlans.paymentPlans, recommendedPlans.paymentPlans);

  if (shouldChangeIdrEnrollment) {
    steps.push({
      title: 'Review consolidation carefully',
      body: (
        <div className={styles.stepBody}>
          <p>
            Consolidation is only helpful when StudentAid.gov confirms it is required for the repayment plan you want.
            Do not consolidate FFEL or Parent PLUS loans in a way that would lose IDR or PSLF eligibility — follow the
            loan-by-loan guidance on StudentAid.gov before consolidating anything.
          </p>
          <p>
            <i>
              If you already have Direct Loans on an IDR plan, consolidating them can reset payment counts. Only
              consolidate the specific loans the enrollment flow identifies.
            </i>
          </p>
        </div>
      ),
      icon: <DebtIcon />,
    });
  }

  if (shouldChangeIdrEnrollment) {
    steps.push({
      title: 'Enroll in Income Driven Repayment (IDR)',
      body: (
        <div className={styles.stepBody}>
          <p>
            {recommendedPlans.isPslf
              ? 'This payment plan is needed to enroll in Public Service Loan Forgiveness (PSLF).'
              : 'This will base your monthly payment on a percentage of your income, reducing your monthly payment.'}
          </p>
          <p>
            <i>
              Note: SAVE is no longer available for new enrollment. Choose RAP, IBR, or another open plan StudentAid.gov
              offers for your loans — do not stay on SAVE-related forbearance if a new plan is open to you.
            </i>
          </p>
          <div className={styles.info}>
            <div className={styles.name}>Plan Name</div>
            <div className={styles.value}>
              {recommendedPlans.paymentPlans.length > 1 ? 'Multiple' : recommendedPlans.paymentPlans[0]}
            </div>
          </div>
          <div className={styles.info}>
            <div className={styles.name}>Monthly Payment Est.</div>
            <div className={styles.value}>
              {recommendedPath?.payments && formatMonetaryAmount(recommendedPath.payments[0])}
            </div>
          </div>
        </div>
      ),
      icon: <PaycheckIcon />,
    });
  }

  if (recommendedPlans.isPslf && !currentPlans.isPslf) {
    const forgivenessDate = addMonths(new Date(), recommendedPath?.payments?.length ?? 0);
    steps.push({
      title: 'Enroll in Public Service Loan Forgiveness',
      body: (
        <div className={styles.stepBody}>
          <p>
            Enroll as soon as possible. You need to make 120 qualifying payments while working at a not for profit
            organization to achieve forgiveness.
          </p>
          <div className={styles.info}>
            <div className={styles.name}>Estimated Date</div>
            <div className={styles.value}>
              {forgivenessDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
            </div>
          </div>
        </div>
      ),

      icon: <GiftIcon />,
    });
  }

  if (statePrograms && statePrograms.length > 0) {
    steps.push({
      title: 'Consider state and service programs',
      body: (
        <div className={styles.stepBody}>
          <p>
            These programs are <strong>conditional if awarded</strong> — competitive or funding-dependent. They are not
            included in your guaranteed federal payment estimate.
          </p>
          <ul className={styles.bulletList}>
            {statePrograms.map((program) => (
              <li key={program.id}>
                <a href={program.applyUrl} target="_blank" rel="noreferrer">
                  {program.name}
                </a>
                <span className={styles.conditionalLabel}>Conditional if awarded</span>
                <p className={styles.programSummary}>{program.summary}</p>
              </li>
            ))}
          </ul>
        </div>
      ),
      icon: <GiftIcon />,
    });
  }

  return (
    <StudentLoanLoaderWrapper>
      <>
        <FormNavigation {...navigationInfo} />
        <FormContainer title="Enroll on StudentAid.gov" subtitle="Follow these steps to maximize your savings.">
          <CardSequence
            steps={[
              {
                center: true,
                title: (
                  <p>
                    Log in to{' '}
                    <a href="https://studentaid.gov/fsa-id/sign-in" target="_blank" rel="noreferrer">
                      studentaid.gov
                    </a>
                    .
                  </p>
                ),
              },
              ...steps,
            ]}
          />

          <div className={styles.buttonContainer}>
            <Button onClick={() => handleNext(StudentLoanApplyOnYourOwnResult.Done)}>Done</Button>
          </div>
        </FormContainer>
      </>
    </StudentLoanLoaderWrapper>
  );
};

const withoutSave = (plans: PlanName[]): PlanName[] => plans.filter((plan) => plan !== PlanShortName.Save);

export const parsePlans = (plans: PlanName[]): { isPslf: boolean; isIdr: boolean; paymentPlans: PlanName[] } => {
  const isPslf = plans.includes(PSLF);
  // SAVE may still appear on a current path from NSLDS mapping, but is never recommended.
  const isIdr =
    plans.includes(PlanShortName.Paye) ||
    plans.includes(PlanShortName.Icr) ||
    plans.includes(PlanShortName.Ibr) ||
    plans.includes(PlanShortName.Save) ||
    plans.includes(PlanShortName.Rap) ||
    plans.includes(PlanShortName.TieredStandard);
  const paymentPlans = plans.filter((plan) => plan !== PSLF && plan !== PlanShortName.Save);
  return { isPslf, isIdr, paymentPlans };
};

export const arePaymentPlansDifferent = (currentPlans: PlanName[], recommendedPlans: PlanName[]) => {
  const currentPaymentPlans = currentPlans.filter((plan) => plan !== PSLF && plan !== PlanShortName.Save);
  const recommendedPaymentPlans = recommendedPlans.filter((plan) => plan !== PSLF && plan !== PlanShortName.Save);
  if (currentPaymentPlans.length !== recommendedPaymentPlans.length) {
    return true;
  }
  return currentPaymentPlans.some((plan, index) => plan !== recommendedPaymentPlans[index]);
};

export default ApplyOnYourOwn;
